import * as Dialog from '@radix-ui/react-dialog';
import { useMemo, useState } from 'react';
import { Download, FileArchive, FileCode, Loader2, X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useAgentStore } from '@/store/agent-store';

type CompressionQuality = 'high' | 'standard' | 'light';

interface QualityOption {
  value: CompressionQuality;
  label: string;
  desc: string;
  /** 相对于原始大小的估算比例（含 ZIP 容器压缩 + 图片重编码） */
  ratio: number;
}

const qualityOptions: QualityOption[] = [
  { value: 'high', label: '高质量', desc: '画质 95%，体积优化约 2–6%', ratio: 0.96 },
  { value: 'standard', label: '标准', desc: '画质 80%，体积优化约 6–15%', ratio: 0.90 },
  { value: 'light', label: '轻量', desc: '画质 55%，体积优化约 12–22%', ratio: 0.82 },
];

function formatSize(bytes: number): string {
  if (!bytes || bytes < 1024) return `${bytes || 0} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

interface DownloadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId?: string;
  fileSize?: number;
}

export function DownloadDialog({ open, onOpenChange, sessionId, fileSize }: DownloadDialogProps): JSX.Element {
  const [quality, setQuality] = useState<CompressionQuality>('standard');
  const [downloading, setDownloading] = useState(false);
  const sessionList = useAgentStore((s) => s.sessionList);

  // 从 store 获取当前会话标题用于下载文件名
  const sessionTitle = useMemo(() => {
    if (!sessionId) return '';
    return sessionList.find((s) => s.id === sessionId)?.title || '';
  }, [sessionId, sessionList]);

  const zipFileName = sessionTitle ? `${sessionTitle}.zip` : `playable-${Date.now()}.zip`;
  const htmlFileName = sessionTitle ? `${sessionTitle}.html` : `playable-${Date.now()}.html`;

  const triggerDownload = (url: string, fileName: string): void => {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const withDownloading = async (fn: () => void): Promise<void> => {
    if (!sessionId) return;
    setDownloading(true);
    try {
      fn();
    } finally {
      setDownloading(false);
      onOpenChange(false);
    }
  };

  const handleDirect = (): Promise<void> =>
    withDownloading(() => triggerDownload(`/api/playable/${sessionId}/download?quality=original`, zipFileName));
  const handleCompress = (): Promise<void> =>
    withDownloading(() => triggerDownload(`/api/playable/${sessionId}/download?quality=${quality}`, zipFileName));
  const handleSingleHtml = (): Promise<void> =>
    withDownloading(() => triggerDownload(`/api/playable/${sessionId}/download-html`, htmlFileName));

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out" />
        <Dialog.Content className="glass-card fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 p-6 data-[state=open]:animate-in data-[state=closed]:animate-out">
          <div className="mb-4 flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold text-foreground">下载试玩包</Dialog.Title>
            <Dialog.Close asChild>
              <button className="icon-btn rounded p-1"><X size={16} /></button>
            </Dialog.Close>
          </div>

          <div className="space-y-4">
            <div>
              <span className="text-xs font-semibold text-muted-foreground">预估包体大小</span>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="neon-text text-lg font-semibold">{formatSize(fileSize ?? 0)}</span>
                <span className="text-[11px] text-muted-foreground">直接下载</span>
              </div>
            </div>

            <div>
              <span className="text-xs font-semibold text-muted-foreground">压缩质量</span>
              <div className="mt-2 space-y-1.5">
                {qualityOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setQuality(opt.value)}
                    className={cn(
                      'flex w-full items-start gap-2.5 rounded-lg border px-3 py-2 text-left transition-colors',
                      quality === opt.value
                        ? 'border-primary/60 bg-primary/10'
                        : 'border-border/40 hover:border-border/80 hover:bg-muted/20',
                    )}
                  >
                    <span className={cn('mt-0.5 h-3.5 w-3.5 shrink-0 rounded-full border-2', quality === opt.value ? 'border-primary bg-primary' : 'border-muted-foreground/50')} />
                    <span className="flex-1">
                      <span className="flex items-center justify-between">
                        <span className="text-sm text-foreground">{opt.label}</span>
                        <span className="text-xs tabular-nums text-muted-foreground">
                          ≈ {formatSize(Math.round((fileSize ?? 0) * opt.ratio))}
                        </span>
                      </span>
                      <span className="block text-[11px] text-muted-foreground">{opt.desc}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-2 pt-1">
              <button onClick={handleDirect} disabled={downloading} className="icon-btn flex items-center gap-1.5 rounded-lg border border-primary/50 bg-primary/5 px-3 py-1.5 text-xs transition-colors hover:bg-primary/10 disabled:opacity-50">
                <FileArchive size={14} />直接下载
              </button>
              <button onClick={handleSingleHtml} disabled={downloading} className="icon-btn flex items-center gap-1.5 rounded-lg border border-primary/50 bg-primary/5 px-3 py-1.5 text-xs transition-colors hover:bg-primary/10 disabled:opacity-50">
                <FileCode size={14} />下载单 HTML
              </button>
              <button onClick={handleCompress} disabled={downloading} className="icon-btn flex items-center gap-1.5 rounded-lg border border-primary/50 bg-primary/5 px-3 py-1.5 text-xs font-medium transition-colors hover:bg-primary/10 disabled:opacity-50">
                {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}压缩并下载
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
